import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getTexcumarSalas, saveTexcumarSalas } from '../../utils/api';
import { baseLabel, formatValue, roomLabel } from './lib/format';
import { STATUS_LABEL, statusFor, tramosDeBanda, validarBanda } from './lib/status';

/* --------------------------------------------------------------------- */
/* Pantalla de configuración de Texcumar                                  */
/*                                                                        */
/* Edita el catálogo de salas: nombre, tipo, calibración y bandas de color. */
/* Los valores se guardan en la configuración global de la aplicación.     */
/* --------------------------------------------------------------------- */

// Métricas que admiten banda de color, con la clave de umbral que les toca.
const METRICAS_BANDA = [
  { threshold: 'oxygen', label: 'Oxígeno disuelto', unit: 'mg/L', aplica: 'cultivo' },
  { threshold: 'temperature', label: 'Temperatura', unit: '°C', aplica: 'ambos' },
  { threshold: 'salinity', label: 'Salinidad', unit: 'ppt', aplica: 'referencia' },
];

// Métricas que admiten desplazamiento de calibración.
const METRICAS_CALIBRACION = [
  { key: 'temperatura_do', label: 'Temperatura', unit: '°C' },
  { key: 'sensor_temperature', label: 'Agua de mar', unit: '°C' },
  { key: 'salinity_ppt', label: 'Salinidad', unit: 'ppt' },
  { key: 'ambient_temperature', label: 'Ambiente', unit: '°C' },
];

const CAMPOS_BANDA = [
  { key: 'min', label: 'Mínimo', ayuda: 'Por debajo: rojo' },
  { key: 'idealMin', label: 'Ideal desde', ayuda: 'Empieza el verde' },
  { key: 'idealMax', label: 'Ideal hasta', ayuda: 'Termina el verde' },
  { key: 'max', label: 'Máximo', ayuda: 'Por encima: rojo' },
];

/* Barra de color de una banda. Es la traducción visual de statusFor(): los
 * tramos salen de la misma definición que evalúa el semáforo, de modo que lo
 * que se ve aquí es exactamente lo que va a pintar la pantalla. */
function BandaPreview({ banda, unit }) {
  const tramos = tramosDeBanda(banda);
  if (!tramos.length) return <div className="tx-banda-preview is-invalid">Banda incompleta</div>;

  return (
    <div className="tx-banda">
      <div className="tx-banda-preview" role="img" aria-label="Vista previa de los colores de la banda">
        {tramos.map((tramo) => (
          <span
            key={`${tramo.status}-${tramo.from}`}
            className={`tx-banda-tramo tx-banda-${tramo.status}`}
            style={{ left: `${tramo.offset}%`, width: `${tramo.width}%` }}
            title={`${STATUS_LABEL[tramo.status]}: ${formatValue(tramo.from, 1)} a ${formatValue(tramo.to, 1)} ${unit}`}
          />
        ))}
      </div>
      <div className="tx-banda-escala">
        <span>{formatValue(banda.min, 1)}</span>
        <span>{formatValue(banda.idealMin, 1)}</span>
        <span>{formatValue(banda.idealMax, 1)}</span>
        <span>{formatValue(banda.max, 1)}</span>
      </div>
    </div>
  );
}

function EditorBanda({ banda, unit, actual, onChange }) {
  const error = validarBanda(banda);
  const estado = actual === null || actual === undefined ? null : statusFor(actual, banda);

  return (
    <div className="tx-config-banda">
      <div className="tx-config-campos">
        {CAMPOS_BANDA.map((campo) => (
          <label key={campo.key}>
            <span>{campo.label}</span>
            <input
              type="number"
              step="0.1"
              value={banda?.[campo.key] ?? ''}
              onChange={(event) => onChange(campo.key, event.target.value)}
            />
            <em>{campo.ayuda}</em>
          </label>
        ))}
      </div>

      {error ? (
        <p className="tx-config-error">{error}</p>
      ) : (
        <>
          <BandaPreview banda={banda} unit={unit} />
          {estado && (
            /* La lectura actual sobre la banda que se está editando: se ve el
               efecto del cambio antes de guardarlo. */
            <p className={`tx-config-actual tx-cell-${estado}`}>
              Lectura actual {formatValue(actual, 1)} {unit} · <strong>{STATUS_LABEL[estado]}</strong>
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function TexcumarConfigPanel({ isOpen, onClose, rooms, onSaved }) {
  const [salas, setSalas] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [bandasBase, setBandasBase] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);
  const [abierta, setAbierta] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelado = false;
    setCargando(true);
    setError(null);
    setOk(false);
    getTexcumarSalas()
      .then((resultado) => {
        if (cancelado) return;
        setSalas(resultado.salas || {});
        setDefaults(resultado.defaults || {});
        setBandasBase(resultado.bandas || null);
      })
      .catch((err) => { if (!cancelado) setError(err.message || 'No se pudo cargar la configuración.'); })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [isOpen]);

  // Series conocidas: las del catálogo más las que estén publicando. Una sala
  // nueva aparece aquí para poder nombrarla, aunque nadie la haya catalogado.
  const series = useMemo(() => {
    const mapa = new Map();
    Object.keys(salas || {}).forEach((serie) => mapa.set(serie, { serie }));
    (rooms || []).forEach((room) => mapa.set(room.serie, room));
    return [...mapa.values()].sort((a, b) => {
      const oa = salas?.[a.serie]?.orden ?? 99;
      const ob = salas?.[b.serie]?.orden ?? 99;
      if (oa !== ob) return oa - ob;
      return a.serie.localeCompare(b.serie);
    });
  }, [salas, rooms]);

  const roomPorSerie = useMemo(() => {
    const mapa = new Map();
    (rooms || []).forEach((room) => mapa.set(room.serie, room));
    return mapa;
  }, [rooms]);

  const entradaDe = useCallback((serie) => {
    const guardada = salas?.[serie];
    if (guardada) return guardada;
    return defaults?.[serie] || { nombre: null, tipo: 'cultivo' };
  }, [salas, defaults]);

  const bandaDe = useCallback((serie, threshold) => {
    const entrada = entradaDe(serie);
    if (entrada.umbrales?.[threshold]) return entrada.umbrales[threshold];
    const base = entrada.tipo === 'referencia' ? bandasBase?.referencia : bandasBase?.cultivo;
    return base?.[threshold] || null;
  }, [entradaDe, bandasBase]);

  const actualizar = (serie, cambio) => {
    setOk(false);
    setSalas((actual) => ({
      ...(actual || {}),
      [serie]: { ...entradaDe(serie), ...cambio },
    }));
  };

  const actualizarBanda = (serie, threshold, campo, valor) => {
    const banda = { ...(bandaDe(serie, threshold) || {}) };
    banda[campo] = valor === '' ? '' : Number(valor);
    const entrada = entradaDe(serie);
    actualizar(serie, { umbrales: { ...(entrada.umbrales || {}), [threshold]: banda } });
  };

  const actualizarCalibracion = (serie, metrica, valor) => {
    const entrada = entradaDe(serie);
    const calibracion = { ...(entrada.calibracion || {}) };
    if (valor === '') delete calibracion[metrica];
    else calibracion[metrica] = Number(valor);
    actualizar(serie, { calibracion });
  };

  const restaurar = (serie) => {
    const semilla = defaults?.[serie];
    if (!semilla) return;
    setOk(false);
    setSalas((actual) => ({ ...(actual || {}), [serie]: { ...semilla } }));
  };

  // Errores de banda de todas las series: bloquean el guardado.
  const errores = useMemo(() => {
    const lista = [];
    series.forEach(({ serie }) => {
      const entrada = entradaDe(serie);
      Object.entries(entrada.umbrales || {}).forEach(([threshold, banda]) => {
        const problema = validarBanda(banda);
        if (problema) lista.push(`${entrada.nombre || serie} · ${threshold}: ${problema}`);
      });
    });
    return lista;
  }, [series, entradaDe]);

  const guardar = async () => {
    if (errores.length) return;
    setGuardando(true);
    setError(null);
    try {
      const resultado = await saveTexcumarSalas(salas || {});
      setSalas(resultado.salas || salas);
      setOk(true);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.message || 'No se pudo guardar la configuración.');
    } finally {
      setGuardando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="tx-config-overlay" role="dialog" aria-modal="true" aria-label="Configuración de Texcumar">
      <div className="tx-config-panel">
        <header className="tx-config-header">
          <div>
            <span className="tx-block-tag">Configuración</span>
            <h2>Salas, umbrales y calibración</h2>
            <p>Configura los umbrales por sala y métrica. Los cambios se aplican al próximo refresco.</p>
          </div>
          <button type="button" className="tx-secondary" onClick={onClose}>Cerrar</button>
        </header>

        <div className="tx-config-leyenda">
          <span className="tx-cell-ok">■ Verde: dentro del rango ideal</span>
          <span className="tx-cell-warning">■ Amarillo: fuera del ideal, aún dentro de límites</span>
          <span className="tx-cell-critical">■ Rojo: fuera de los límites</span>
        </div>

        {error && <div className="tx-error">{error}</div>}
        {ok && <div className="tx-config-ok">Configuración guardada. La pantalla la aplica en el próximo refresco.</div>}

        {cargando ? (
          <div className="tx-empty">Cargando configuración…</div>
        ) : (
          <div className="tx-config-lista">
            {series.map(({ serie }) => {
              const entrada = entradaDe(serie);
              const room = roomPorSerie.get(serie);
              const desplegada = abierta === serie;
              const bandasVisibles = METRICAS_BANDA.filter((m) => m.aplica === 'ambos'
                || (m.aplica === 'referencia' ? entrada.tipo === 'referencia' : entrada.tipo !== 'referencia'));

              return (
                <section key={serie} className="tx-config-sala">
                  <header>
                    <button
                      type="button"
                      className="tx-config-toggle"
                      onClick={() => setAbierta(desplegada ? null : serie)}
                      aria-expanded={desplegada}
                    >
                      <strong>{entrada.nombre || serie}</strong>
                      <em>
                        {room ? `${baseLabel(room.base_code)} · ${roomLabel(room.sala)}` : serie}
                      </em>
                      <span aria-hidden="true">{desplegada ? '−' : '+'}</span>
                    </button>
                  </header>

                  {desplegada && (
                    <div className="tx-config-cuerpo">
                      <div className="tx-config-campos">
                        <label>
                          <span>Nombre</span>
                          <input
                            type="text"
                            maxLength={60}
                            value={entrada.nombre || ''}
                            placeholder="Sala U"
                            onChange={(event) => actualizar(serie, { nombre: event.target.value })}
                          />
                          <em>Como aparece en la pantalla</em>
                        </label>
                        <label>
                          <span>Tipo</span>
                          <select
                            value={entrada.tipo || 'cultivo'}
                            onChange={(event) => actualizar(serie, { tipo: event.target.value })}
                          >
                            <option value="cultivo">Sala de cultivo</option>
                            <option value="referencia">Referencia</option>
                          </select>
                          <em>Las referencias no cuentan en el semáforo</em>
                        </label>
                      </div>

                      <h4>Calibración</h4>
                      <p className="tx-config-nota">
                        Se aplica solo a lo que se muestra. El dato original se conserva
                        intacto en la base y en la tabla de detalle.
                      </p>
                      <div className="tx-config-campos">
                        {METRICAS_CALIBRACION.map((metrica) => (
                          <label key={metrica.key}>
                            <span>{metrica.label}</span>
                            <input
                              type="number"
                              step="0.1"
                              placeholder="0"
                              value={entrada.calibracion?.[metrica.key] ?? ''}
                              onChange={(event) => actualizarCalibracion(serie, metrica.key, event.target.value)}
                            />
                            <em>Desplazamiento en {metrica.unit}</em>
                          </label>
                        ))}
                      </div>

                      {bandasVisibles.map((metrica) => (
                        <div key={metrica.threshold}>
                          <h4>{metrica.label} <small>({metrica.unit})</small></h4>
                          <EditorBanda
                            banda={bandaDe(serie, metrica.threshold)}
                            unit={metrica.unit}
                            actual={room ? room[`current_${metricaActual(metrica.threshold, entrada.tipo)}`] : null}
                            onChange={(campo, valor) => actualizarBanda(serie, metrica.threshold, campo, valor)}
                          />
                        </div>
                      ))}

                      {defaults?.[serie] && (
                        <button type="button" className="tx-link-button" onClick={() => restaurar(serie)}>
                          Restaurar valores predeterminados para esta sala
                        </button>
                      )}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}

        <footer className="tx-config-footer">
          {errores.length > 0 && (
            <ul className="tx-config-error">
              {errores.slice(0, 4).map((problema) => <li key={problema}>{problema}</li>)}
            </ul>
          )}
          <div className="tx-filters-actions">
            <button type="button" className="tx-secondary" onClick={onClose}>Cancelar</button>
            <button
              type="button"
              className="tx-export"
              onClick={guardar}
              disabled={guardando || cargando || errores.length > 0}
            >
              {guardando ? 'Guardando…' : 'Guardar configuración'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

/* Qué lectura actual corresponde a cada banda, para la vista previa.
 *
 * La temperatura de una sala de cultivo viene del sensor de oxígeno disuelto,
 * y la de una referencia como el agua de mar del sensor propio: son columnas
 * distintas para la misma banda. */
function metricaActual(threshold, tipo) {
  if (threshold === 'saturation') return 'saturacion_pct';
  if (threshold === 'salinity') return 'salinity_ppt';
  return tipo === 'referencia' ? 'sensor_temperature' : 'temperatura_do';
}
